import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { BottleneckState } from '@complan/domain';
import { cn } from '@/lib/utils';
import { BOTTLENECK_STATE_LABEL_KEYS } from '@/shared/i18n/enum-labels';

/**
 * A Bottleneck_State (R6.4) rendered as a colour-coded pill, consistently wherever
 * a capacity state shows: the Capacity & Utilisation reconciliation table, the
 * Capacity Inputs register's incomplete rows and the Demand Explorer's
 * data-incomplete column.
 *
 * The sibling of {@link StatusBadge}, and deliberately built the same way, because
 * the two appear in the same tables and any difference in pill geometry reads as a
 * mistake.
 *
 * ## Colours
 *
 * UI spec §3.4's `t-ok` / `t-warn` / `t-over` / `t-incomplete` tokens, taken
 * verbatim from the approved click dummy's palette: a tinted background with a
 * darker text of the same hue. Inline values rather than Tailwind classes for the
 * same reason `StatusBadge` uses them — the approved hues survive a Tailwind purge
 * and read exactly as the design reference.
 *
 * What this replaces is four hand-rolled `inline-flex rounded-full border … ` pill
 * strings across the Manpower screens, which were `rounded-full` where §3.4
 * specifies a 3px radius, 11px where it specifies 10.5px, and outline-only where
 * it specifies a tint. One of them also set `text-muted`, which is the shadcn
 * *surface* token rather than the muted *text* token, so the "Data incomplete"
 * label rendered near-white on a near-white fill.
 *
 * ## Colour is never the only carrier (R10.6, R18.8)
 *
 * The pill always shows the state as text, so it stays distinguishable when colour
 * is not perceived. The colour is additive to the label, never a replacement.
 *
 * ## A note on §3.4's migration remark
 *
 * The spec asks that `t-incomplete` be aligned to `--grey` (#8794A5) so the badge
 * agrees with the utilisation grid's Data_Incomplete fill. That is applied to the
 * *border* here and not to the text: `--grey` as a text colour on the `#EEF1F5`
 * tint is roughly 2.4:1, well under the 4.5:1 R18.7 requires, so the darker
 * approved text is kept and the grey carries the association instead.
 */
const STATE_COLORS: Record<
  BottleneckState,
  { readonly background: string; readonly text: string; readonly border: string }
> = {
  normal: { background: '#E7F3EE', text: '#14724A', border: 'transparent' },
  warning: { background: '#FDF0DC', text: '#9A5B00', border: 'transparent' },
  over_capacity: { background: '#FBE3E0', text: '#A0281B', border: 'transparent' },
  data_incomplete: { background: '#EEF1F5', text: '#65758A', border: 'var(--grey)' },
};

export interface CapacityStateBadgeProps {
  readonly state: BottleneckState;
  readonly className?: string;
  /**
   * Replaces the state's own name, for the one column that reports a *count* of
   * affected items rather than a single item's state (the Demand Explorer's
   * data-incomplete driver count).
   *
   * A caller that overrides the label owes the pill an `aria-label`, because the
   * label is what carries the meaning when colour is not perceived (R10.6, R18.8)
   * and a bare numeral does not.
   */
  readonly children?: React.ReactNode;
  readonly 'aria-label'?: string;
}

/** A colour-coded Bottleneck_State pill; see the module doc for the palette. */
export function CapacityStateBadge({
  state,
  className,
  children,
  'aria-label': ariaLabel,
}: CapacityStateBadgeProps): React.JSX.Element {
  const { t } = useTranslation();
  const colors = STATE_COLORS[state];

  return (
    <span
      data-slot="capacity-state-badge"
      data-state={state}
      className={cn(
        'inline-flex w-fit shrink-0 items-center rounded-[3px] border px-1.5 py-px text-[10.5px] font-semibold leading-[17px] whitespace-nowrap',
        className,
      )}
      style={{
        backgroundColor: colors.background,
        color: colors.text,
        borderColor: colors.border,
      }}
      {...(ariaLabel !== undefined && { 'aria-label': ariaLabel })}
    >
      {children ?? t(BOTTLENECK_STATE_LABEL_KEYS[state])}
    </span>
  );
}
